import { Box, Skeleton, Flex, Button, Tooltip, Icon } from '@scale/scaleui-radix';
import {
  useGetSessionsForCollection,
  editCollectionInfo,
  editCollectionInvitees,
  editCollectionSessions,
  addSessionsToCollectionV2,
  useSearchUsers,
} from 'react-scale/components/WebinarSessions/api';
import { useRouteMatch } from 'react-router-dom';
import WebinarError from 'react-scale/components/WebinarSessions/components/WebinarError';
import { CollectionSessionWithUserIds } from '@scale/shared/webinarSessions/types';
import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import WebinarFormLayout, {
  NAVIGATION_ITEMS,
  NavigationItems,
} from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarFormLayout';
import WebinarSettingsForm from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarSettingsForm';
import { createEditWebinarSessionStore } from 'react-scale/components/WebinarSessions/hooks/useEditWebinarSessionStore';
import { useStore } from 'zustand';
import {
  WebinarErrorMessages,
  WebinarError as WebinarErrorType,
} from 'react-scale/components/WebinarSessions/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query-v4';
import { toast } from 'react-toastify';
import WebinarInviteeForm from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarInviteeForm';
import WebinarSessionsForm from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarSessionsForm';
import { ExistingSessions } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/ExistingSessions';
import {
  getNewStartDateTime,
  timeDifferenceInMinutes,
} from 'react-scale/components/WebinarSessions/hooks/calculateFutureOccurrences';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import WebinarInviteesSummary from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarInviteesSummary';
import { useUser } from 'react-scale/components/ui/UserContext';
interface EditWebinarFooterProps {
  cancelButtonDisabled: boolean;
  onCancel: () => void;
  webinarErrors: WebinarErrorType[];
  saveButtonDisabled: boolean;
  saveButtonLoading: boolean;
  onSave: () => void;
  saveButtonText?: string;
}

const EditWebinarFooter = ({
  cancelButtonDisabled,
  onCancel,
  webinarErrors,
  saveButtonDisabled,
  saveButtonLoading,
  onSave,
  saveButtonText = 'Save Changes',
}: EditWebinarFooterProps) => {
  return (
    <Flex justify="end" gap="2">
      <Button
        size="2"
        variant="outline"
        color="neutral"
        radius="small"
        disabled={cancelButtonDisabled}
        onClick={onCancel}
      >
        Discard Changes
      </Button>
      <Tooltip
        content={webinarErrors.length > 0 ? WebinarErrorMessages[webinarErrors[0]] : saveButtonText}
        side="bottom"
      >
        <Button
          size="2"
          variant="solid"
          color="accent"
          radius="small"
          onClick={onSave}
          disabled={saveButtonDisabled || webinarErrors.length > 0}
          loading={saveButtonLoading}
        >
          {saveButtonText}
        </Button>
      </Tooltip>
    </Flex>
  );
};

const WebinarEditPage = ({
  collection,
  sessions,
}: {
  collection: CollectionSessionWithUserIds;
  sessions: CollectionSessionWithUserIds[];
}) => {
  const { params, url } = useRouteMatch<{ projectId: string }>();
  const urlParts = url.split('/');
  const webinarsUrl = urlParts.slice(0, urlParts.length - 1).join('/');
  const projectId = params ? params.projectId : null;
  const user = useUser();

  const store = useRef(
    createEditWebinarSessionStore({
      name: collection.name ?? null,
      description: collection.description ?? null,
      project: collection.project ?? null,
      rsvpMax: collection.rsvpMax ?? null,
      minAttendanceDuration: collection.minAttendanceDuration ?? null,
      expectedNumberOfAttendees: collection.expectedNumberOfAttendees ?? null,
      invitedUsers: collection.invitedUsers,
      audience: collection.audience ?? null,
      audienceBucket: collection.audienceBucket ?? null,
      invitedWorkerTeams: collection.invitedWorkerTeams,
      invitedQualifications: collection.invitedQualifications,
      invitedSquads: collection.invitedSquads,
      excludeEmptyQueueUsers: collection.excludeEmptyQueueUsers ?? false,
      hostedBy: user ? [user.id] : [],
    }),
  ).current;

  const {
    name,
    setName,
    description,
    setDescription,
    project,
    setProject,
    rsvpMax,
    setRsvpMax,
    minAttendanceDuration,
    setMinAttendanceDuration,
    expectedNumberOfAttendees,
    setExpectedNumberOfAttendees,
    resetWebinarSettingFields,
    getWebinarSettingsChanges,
    invitedUsers,
    setInvitedUsers,
    audience,
    setAudience,
    audienceBucket,
    setAudienceBucket,
    invitedWorkerTeams,
    setInvitedWorkerTeams,
    invitedQualifications,
    setInvitedQualifications,
    invitedSquads,
    setInvitedSquads,
    excludeEmptyQueueUsers,
    setExcludeEmptyQueueUsers,
    allInvitedUserIds,
    setAllInvitedUserIds,
    resetWebinarInviteeFields,
    getWebinarInviteeChanges,
    sessionType,
    setSessionType,
    hostedBy,
    setHostedBy,
    occurrences,
    setOccurrences,
    numOccurrences,
    setNumOccurrences,
    repeatNumber,
    setRepeatNumber,
    repeatType,
    setRepeatType,
    daysOfTheWeek,
    setDaysOfTheWeek,
    additionalTimesOfDay,
    setAdditionalTimesOfDay,
    eventStart,
    setEventStart,
    duration,
    setDuration,
    selectedDate,
    setSelectedDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    additionalSessions,
    setAdditionalSessions,
    createNewSessions,
    setCreateNewSessions,
    sessionIdsToCancel,
    setSessionIdsToCancel,
    sessionIdsWithHostChanges,
    setSessionIdsWithHostChanges,
    resetWebinarSessionFields,
    resetWebinarSessionFieldsOnEdit,
    resetWebinarSessionFieldsOnCreate,
    getWebinarSessionChanges,
    getErrors,
    getRecurringErrors,
    getAdditionalSessionsErrors,
  } = useStore(store);

  const [sectionIndex, setSectionIndex] = useState<number>(0);
  const currentNav = NAVIGATION_ITEMS[sectionIndex];

  const webinarErrors = getErrors(NAVIGATION_ITEMS[sectionIndex]);
  const webinarSettingsChanges = getWebinarSettingsChanges(collection);
  const webinarSettingsChanged = Object.values(webinarSettingsChanges).some(Boolean);
  const webinarInviteeChanges = getWebinarInviteeChanges(collection);
  const webinarInviteeChanged = Object.values(webinarInviteeChanges).some(Boolean);
  const webinarSessionChanges = getWebinarSessionChanges();
  const areSessionsChanged =
    webinarSessionChanges.isEventStartChanged ||
    webinarSessionChanges.isOccurrencesChanged ||
    webinarSessionChanges.isAdditionalSessionsChanged;
  const areExistingSessionsChanged =
    webinarSessionChanges.isSessionIdsToCancelChanged || webinarSessionChanges.isHostedByChanged;

  const { data: searchUsersResponse, isLoading: isLoadingAllInvitedUserIds } = useSearchUsers({
    userIds: invitedUsers,
    workerTeamIds: invitedWorkerTeams,
    squadIds: invitedSquads,
    qualificationIds: invitedQualifications,
    audienceId: audience ?? undefined,
    audienceBucket: audienceBucket ?? undefined,
    excludeEmptyQueueUsers,
  });

  useEffect(() => {
    if (!isLoadingAllInvitedUserIds) {
      setAllInvitedUserIds(searchUsersResponse?.userIds ?? []);
    }
  }, [searchUsersResponse, isLoadingAllInvitedUserIds, setAllInvitedUserIds]);

  const queryClient = useQueryClient();
  const { mutate: editCollectionInfoMutation, isLoading: isEditingCollectionInfo } = useMutation({
    mutationFn: editCollectionInfo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['useGetSessionsForCollection', collection._id],
      });
      queryClient.invalidateQueries({
        queryKey: ['useGetSessionsForCollectionWithHydratedUsers', collection._id],
      });

      toast.success('Webinar settings updated successfully!');
    },
  });
  const { mutate: editCollectionInviteesMutation, isLoading: isEditingCollectionInvitees } =
    useMutation({
      mutationFn: editCollectionInvitees,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['useGetSessionsForCollection', collection._id],
        });
        queryClient.invalidateQueries({
          queryKey: ['useGetSessionsForCollectionWithHydratedUsers', collection._id],
        });

        toast.success('Webinar invitees updated successfully!');
      },
    });
  const { mutate: editCollectionSessionsMutation, isLoading: isEditingCollectionSessions } =
    useMutation({
      mutationFn: editCollectionSessions,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['useGetSessionsForCollection', collection._id],
        });
        queryClient.invalidateQueries({
          queryKey: ['useGetSessionsForCollectionWithHydratedUsers', collection._id],
        });

        toast.success('Webinar sessions updated successfully!');
        resetWebinarSessionFieldsOnEdit();
      },
    });
  const { mutate: addSessionsToCollectionMutation, isLoading: isAddingCollectionSessions } =
    useMutation({
      mutationFn: addSessionsToCollectionV2,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['useGetSessionsForCollection', collection._id],
        });
        queryClient.invalidateQueries({
          queryKey: ['useGetSessionsForCollectionWithHydratedUsers', collection._id],
        });

        toast.success('New webinar sessions created successfully!');
        resetWebinarSessionFieldsOnCreate();
      },
    });

  const currentSectionChanged = useMemo(() => {
    const currentNav = NAVIGATION_ITEMS[sectionIndex];
    if (currentNav === NavigationItems.WEBINAR_SETTINGS) {
      return webinarSettingsChanged;
    } else if (currentNav === NavigationItems.SELECT_INVITEE) {
      return webinarInviteeChanged;
    } else if (createNewSessions) {
      return areSessionsChanged || areExistingSessionsChanged;
    } else {
      return areExistingSessionsChanged;
    }
  }, [
    sectionIndex,
    webinarSettingsChanged,
    createNewSessions,
    areSessionsChanged,
    areExistingSessionsChanged,
    webinarInviteeChanged,
  ]);

  const saveButtonText = useMemo(() => {
    if (currentNav === NavigationItems.SESSION_SETTINGS && createNewSessions) {
      return areExistingSessionsChanged
        ? 'Save Changes and Create New Sessions'
        : 'Create New Sessions';
    }
    return 'Save Changes';
  }, [createNewSessions, areExistingSessionsChanged, currentNav]);

  const onCancel = useCallback(() => {
    if (currentNav === NavigationItems.WEBINAR_SETTINGS) {
      resetWebinarSettingFields(collection);
    } else if (currentNav === NavigationItems.SELECT_INVITEE) {
      resetWebinarInviteeFields(collection);
    } else {
      resetWebinarSessionFields();
    }
  }, [
    resetWebinarInviteeFields,
    resetWebinarSettingFields,
    resetWebinarSessionFields,
    collection,
    currentNav,
  ]);

  const saveButtonLoading = useMemo(() => {
    if (currentNav === NavigationItems.WEBINAR_SETTINGS) {
      return isEditingCollectionInfo;
    } else if (currentNav === NavigationItems.SELECT_INVITEE) {
      return isEditingCollectionInvitees;
    } else {
      return isAddingCollectionSessions || isEditingCollectionSessions;
    }
  }, [
    currentNav,
    isEditingCollectionInfo,
    isEditingCollectionInvitees,
    isAddingCollectionSessions,
    isEditingCollectionSessions,
  ]);

  const onSave = useCallback(() => {
    const currentNav = NAVIGATION_ITEMS[sectionIndex];
    if (currentNav === NavigationItems.WEBINAR_SETTINGS) {
      const {
        isNameChanged,
        isDescriptionChanged,
        isRsvpMaxChanged,
        isMinAttendanceDurationChanged,
        isProjectChanged,
        isExpectedNumberOfAttendeesChanged,
      } = webinarSettingsChanges;

      const newProjectId = project && project.length > 0 ? project : null;
      editCollectionInfoMutation({
        collectionId: collection._id,
        params: {
          name: isNameChanged ? name : undefined,
          description: isDescriptionChanged ? description : undefined,
          project: isProjectChanged ? newProjectId : undefined,
          rsvpMax: isRsvpMaxChanged ? rsvpMax : undefined,
          minAttendanceDuration: isMinAttendanceDurationChanged ? minAttendanceDuration : undefined,
          expectedNumberOfAttendees: isExpectedNumberOfAttendeesChanged
            ? expectedNumberOfAttendees
            : undefined,
        },
      });
    } else if (currentNav === NavigationItems.SELECT_INVITEE) {
      const { isAudienceChanged, isAudienceBucketChanged } = webinarInviteeChanges;

      const newAudience = audience && audience.length > 0 ? audience : null;
      editCollectionInviteesMutation({
        collectionId: collection._id,
        params: {
          invitedUsers,
          invitedWorkerTeams,
          invitedQualifications,
          invitedSquads,
          audience: isAudienceChanged ? newAudience : undefined,
          audienceBucket: isAudienceBucketChanged ? audienceBucket : undefined,
          excludeEmptyQueueUsers,
        },
      });
    } else {
      if (createNewSessions) {
        addSessionsToCollectionMutation({
          collectionId: collection._id,
          params: {
            hostedBy,
            occurrences: occurrences.map(o => ({
              eventStart: getNewStartDateTime(o.date, o.startTime).toJSON(),
              duration: timeDifferenceInMinutes(o.endTime, o.startTime),
              hostedBy: o.hostedBy,
            })),
            additionalSessions: additionalSessions.map(o => ({
              eventStart: getNewStartDateTime(o.date, o.startTime).toJSON(),
              duration: timeDifferenceInMinutes(o.endTime, o.startTime),
              hostedBy: o.hostedBy,
            })),
            recurringType: sessionType,
            eventStart: eventStart?.toISOString(),
            duration: duration ?? 0,
          },
        });
      }

      if (areExistingSessionsChanged) {
        editCollectionSessionsMutation({
          collectionId: collection._id,
          params: {
            sessionIdsToCancel,
            sessionIdsWithHostChanges,
          },
        });
      }
    }
  }, [
    sectionIndex,
    collection._id,
    webinarSettingsChanges,
    name,
    description,
    project,
    rsvpMax,
    minAttendanceDuration,
    expectedNumberOfAttendees,
    editCollectionInfoMutation,
    webinarInviteeChanges,
    audience,
    audienceBucket,
    invitedUsers,
    invitedWorkerTeams,
    invitedQualifications,
    invitedSquads,
    editCollectionInviteesMutation,
    addSessionsToCollectionMutation,
    hostedBy,
    occurrences,
    additionalSessions,
    sessionType,
    eventStart,
    duration,
    createNewSessions,
    editCollectionSessionsMutation,
    sessionIdsToCancel,
    sessionIdsWithHostChanges,
    areExistingSessionsChanged,
    excludeEmptyQueueUsers,
  ]);

  return (
    <WebinarFormLayout
      sectionIndex={sectionIndex}
      setSectionIndex={setSectionIndex}
      webinarName={name}
      backUrl={webinarsUrl}
      backText="Back to webinar lookup"
      webinarSettingsContent={
        <WebinarSettingsForm
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          project={project}
          setProject={setProject}
          rsvpMax={rsvpMax}
          setRsvpMax={setRsvpMax}
          minAttendanceDuration={minAttendanceDuration}
          setMinAttendanceDuration={setMinAttendanceDuration}
          expectedNumberOfAttendees={expectedNumberOfAttendees}
          setExpectedNumberOfAttendees={setExpectedNumberOfAttendees}
          webinarErrors={webinarErrors}
          duration={null}
          isProjectView={!!projectId}
        />
      }
      sessionSettingsContent={
        <Flex direction="column" gap="4">
          <ExistingSessions
            sessions={sessions}
            sessionIdsToCancel={sessionIdsToCancel}
            setSessionIdsToCancel={setSessionIdsToCancel}
            sessionIdsWithHostChanges={sessionIdsWithHostChanges}
            setSessionIdsWithHostChanges={setSessionIdsWithHostChanges}
          />

          {!createNewSessions && (
            <Button
              size="2"
              variant="outline"
              color="accent"
              radius="small"
              className="w-fit"
              onClick={() => {
                setCreateNewSessions(true);
              }}
            >
              <Icon icon={faPlus} size="2" label="plus" />
              Add additional sessions
            </Button>
          )}

          {createNewSessions && (
            <WebinarSessionsForm
              sessionType={sessionType}
              setSessionType={setSessionType}
              setDuration={setDuration}
              eventStart={eventStart}
              setEventStart={setEventStart}
              numOccurrences={numOccurrences}
              setNumOccurrences={setNumOccurrences}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              hostedBy={hostedBy}
              setHostedBy={setHostedBy}
              repeatNumber={repeatNumber}
              setRepeatNumber={setRepeatNumber}
              repeatType={repeatType}
              setRepeatType={setRepeatType}
              daysOfTheWeek={daysOfTheWeek}
              setDaysOfTheWeek={setDaysOfTheWeek}
              additionalSessions={additionalSessions}
              setAdditionalSessions={setAdditionalSessions}
              occurencesV2={occurrences}
              setOccurencesV2={setOccurrences}
              additionalTimesOfDayV2={additionalTimesOfDay}
              setAdditionalTimesOfDayV2={setAdditionalTimesOfDay}
              getErrors={() => getErrors(NAVIGATION_ITEMS[sectionIndex])}
              getRecurringErrors={getRecurringErrors}
              getAdditionalSessionsErrors={getAdditionalSessionsErrors}
            />
          )}
        </Flex>
      }
      selectInviteeContent={
        <WebinarInviteeForm
          project={project}
          invitedWorkerTeams={invitedWorkerTeams}
          setInvitedWorkerTeams={setInvitedWorkerTeams}
          invitedUsers={invitedUsers}
          setInvitedUsers={setInvitedUsers}
          invitedQualifications={invitedQualifications}
          setInvitedQualifications={setInvitedQualifications}
          invitedSquads={invitedSquads}
          setInvitedSquads={setInvitedSquads}
          audience={audience}
          setAudience={setAudience}
          audienceBucket={audienceBucket}
          setAudienceBucket={setAudienceBucket}
          webinarErrors={webinarErrors}
          excludeEmptyQueueUsers={excludeEmptyQueueUsers}
          setExcludeEmptyQueueUsers={setExcludeEmptyQueueUsers}
        />
      }
      inviteeSummaryContent={
        <WebinarInviteesSummary
          invitedUsers={invitedUsers}
          setInvitedUsers={setInvitedUsers}
          allInvitedUserIds={allInvitedUserIds}
        />
      }
      footerContent={
        <EditWebinarFooter
          cancelButtonDisabled={!currentSectionChanged}
          onCancel={onCancel}
          webinarErrors={webinarErrors}
          saveButtonDisabled={!currentSectionChanged}
          saveButtonLoading={saveButtonLoading}
          onSave={onSave}
          saveButtonText={saveButtonText}
        />
      }
    />
  );
};

const EditWebinarView = () => {
  const { params } = useRouteMatch<{ id: string }>();
  const collectionId = params.id;
  const { data, isLoading, error } = useGetSessionsForCollection({ collectionId });

  if (isLoading) {
    return (
      <Box p="8">
        <Flex direction="row" gap="4" justify="center">
          <Skeleton width="200px" height="120px" />
          <Skeleton width="800px" height="450px" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return <WebinarError error={error} />;
  }

  if (!data) {
    return (
      <WebinarError error={new Error(`No webinar data found for collection id ${collectionId}`)} />
    );
  }

  const collection = data.sessions.find(session => {
    return session._id === collectionId;
  });

  if (!collection) {
    return (
      <WebinarError
        error={new Error(`No webinar collection found for collection id ${collectionId}`)}
      />
    );
  }

  return <WebinarEditPage collection={collection} sessions={data.sessions} />;
};

export default EditWebinarView;
